import { useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { Palette, Search, Check, X, Globe, Shield, ExternalLink } from "lucide-react";
import { format } from "date-fns";

import { useAuth } from "@/hooks/useAuth";
import { useAdminAgencyBranding, AdminAgencyBrandingRow } from "@/hooks/useAdminBranding";

import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { BrandingPreview } from "@/components/agency/branding/BrandingPreview";

const DOMAIN_STATUS_OPTIONS = ["all", "verified", "pending", "none"] as const;

function getDomainStatusVariant(status: string | null): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "verified":
      return "default";
    case "pending":
      return "secondary";
    default:
      return "outline";
  }
}

function getSSLStatusVariant(status: string | null): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
    case "issued":
      return "default";
    case "pending":
      return "secondary";
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
}

export default function BrandingOverviewPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const { data: agencies, isLoading, error } = useAdminAgencyBranding();
  const isMobile = useIsMobile();

  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState<string>("all");
  const [selectedAgency, setSelectedAgency] = useState<AdminAgencyBrandingRow | null>(null);

  // Domain statistics
  const domainStats = useMemo(() => {
    if (!agencies) return { total: 0, withDomain: 0, verified: 0, pendingSSL: 0 };
    return {
      total: agencies.length,
      withDomain: agencies.filter(a => a.custom_domain).length,
      verified: agencies.filter(a => a.domain_status === "verified").length,
      pendingSSL: agencies.filter(a => a.ssl_status === "pending").length,
    };
  }, [agencies]);

  // Filter agencies
  const filteredAgencies = useMemo(() => {
    if (!agencies) return [];
    return agencies.filter((a) => {
      const matchesSearch =
        search === "" ||
        a.agency_email.toLowerCase().includes(search.toLowerCase()) ||
        (a.custom_domain && a.custom_domain.toLowerCase().includes(search.toLowerCase()));
      
      let matchesDomain = true;
      if (domainFilter === "verified") {
        matchesDomain = a.domain_status === "verified";
      } else if (domainFilter === "pending") {
        matchesDomain = a.domain_status === "pending";
      } else if (domainFilter === "none") {
        matchesDomain = !a.custom_domain;
      }
      
      return matchesSearch && matchesDomain;
    });
  }, [agencies, search, domainFilter]);

  // Access control: only platform admins
  if (authLoading) {
    return (
      <div className="container py-6">
        <TableSkeleton rows={8} columns={6} />
      </div>
    );
  }

  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return (
      <div className="container py-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Agency Branding</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Palette className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Branding Overview</h1>
          <p className="text-muted-foreground">
            View all agency branding configurations and custom domains (read-only)
          </p>
        </div>
      </div>

      {/* Domain Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{domainStats.total}</div>
            <p className="text-sm text-muted-foreground">Total Agencies</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{domainStats.withDomain}</div>
            <p className="text-sm text-muted-foreground">With Custom Domain</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{domainStats.verified}</div>
            <p className="text-sm text-muted-foreground">Domains Verified</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-yellow-600">{domainStats.pendingSSL}</div>
            <p className="text-sm text-muted-foreground">SSL Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or domain..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={domainFilter} onValueChange={setDomainFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by domain" />
          </SelectTrigger>
          <SelectContent>
            {DOMAIN_STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s === "all" ? "All Statuses" : s === "none" ? "No Domain" : s.charAt(0).toUpperCase() + s.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">
        Showing {filteredAgencies.length} of {agencies?.length || 0} agencies
      </p>

      {/* Loading state */}
      {isLoading && <TableSkeleton rows={8} columns={6} />}

      {/* Desktop Table */}
      {!isLoading && !isMobile && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agency Email</TableHead>
                <TableHead>Custom Domain</TableHead>
                <TableHead>Domain Status</TableHead>
                <TableHead>SSL Status</TableHead>
                <TableHead>Logo Set</TableHead>
                <TableHead>Colors Set</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAgencies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No agencies found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAgencies.map((agency) => (
                  <TableRow
                    key={agency.agency_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedAgency(agency)}
                  >
                    <TableCell className="font-medium">{agency.agency_email}</TableCell>
                    <TableCell>
                      {agency.custom_domain ? (
                        <div className="flex items-center gap-1">
                          <Globe className="h-3 w-3 text-muted-foreground" />
                          <span className="font-mono text-sm">{agency.custom_domain}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {agency.domain_status ? (
                        <Badge variant={getDomainStatusVariant(agency.domain_status)}>
                          {agency.domain_status}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {agency.ssl_status ? (
                        <Badge variant={getSSLStatusVariant(agency.ssl_status)}>
                          <Shield className="h-3 w-3 mr-1" />
                          {agency.ssl_status}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {agency.has_logo ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      {agency.has_custom_colors ? (
                        <div className="flex items-center gap-1">
                          <div
                            className="h-4 w-4 rounded-full border"
                            style={{ backgroundColor: agency.primary_color }}
                          />
                          <div
                            className="h-4 w-4 rounded-full border"
                            style={{ backgroundColor: agency.secondary_color }}
                          />
                        </div>
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Mobile Card View */}
      {!isLoading && isMobile && (
        <div className="space-y-3">
          {filteredAgencies.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No agencies found
              </CardContent>
            </Card>
          ) : (
            filteredAgencies.map((agency) => (
              <Card
                key={agency.agency_id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setSelectedAgency(agency)}
              >
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{agency.agency_email}</p>
                      {agency.custom_domain && (
                        <p className="text-sm text-muted-foreground font-mono flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          {agency.custom_domain}
                        </p>
                      )}
                    </div>
                    {agency.domain_status && (
                      <Badge variant={getDomainStatusVariant(agency.domain_status)}>
                        {agency.domain_status}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      Logo: {agency.has_logo ? <Check className="h-3 w-3 text-green-600" /> : <X className="h-3 w-3" />}
                    </span>
                    <span className="flex items-center gap-1">
                      Colors: {agency.has_custom_colors ? (
                        <div className="flex gap-0.5">
                          <div className="h-3 w-3 rounded-full border" style={{ backgroundColor: agency.primary_color }} />
                          <div className="h-3 w-3 rounded-full border" style={{ backgroundColor: agency.secondary_color }} />
                        </div>
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Detail Sheet with BrandingPreview */}
      <Sheet open={!!selectedAgency} onOpenChange={() => setSelectedAgency(null)}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Branding Preview
            </SheetTitle>
            <SheetDescription>{selectedAgency?.agency_email} (read-only)</SheetDescription>
          </SheetHeader>

          {selectedAgency && (
            <div className="mt-6 space-y-6">
              {/* Domain Info */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Custom Domain</h3>
                {selectedAgency.custom_domain ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span className="font-mono">{selectedAgency.custom_domain}</span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <div className="flex gap-2">
                      <Badge variant={getDomainStatusVariant(selectedAgency.domain_status)}>
                        {selectedAgency.domain_status}
                      </Badge>
                      {selectedAgency.ssl_status && (
                        <Badge variant={getSSLStatusVariant(selectedAgency.ssl_status)}>
                          <Shield className="h-3 w-3 mr-1" />
                          SSL: {selectedAgency.ssl_status}
                        </Badge>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No custom domain configured</p>
                )}
              </div>

              {/* Branding Details */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Branding Settings</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Font Family</p>
                    <p style={{ fontFamily: selectedAgency.font_family }}>{selectedAgency.font_family}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Allow Tenant Branding</p>
                    <p>{selectedAgency.allow_tenant_branding ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email From Name</p>
                    <p>{selectedAgency.email_from_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Support Email</p>
                    <p>{selectedAgency.support_email || "—"}</p>
                  </div>
                </div>
                {selectedAgency.branding_updated_at && (
                  <p className="text-xs text-muted-foreground">
                    Last updated: {format(new Date(selectedAgency.branding_updated_at), "PPP")}
                  </p>
                )}
              </div>

              {/* Live Preview */}
              <BrandingPreview
                branding={{
                  logo_url: selectedAgency.logo_url,
                  primary_color: selectedAgency.primary_color,
                  secondary_color: selectedAgency.secondary_color,
                  font_family: selectedAgency.font_family,
                  login_message: selectedAgency.login_message,
                  dashboard_footer: selectedAgency.dashboard_footer,
                  powered_by_text: selectedAgency.powered_by_text,
                }}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
